import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router";
import SecondaryButton from "components/Global/Buttons/SecondaryButton";
import { ButtonText } from "@loanbossinc/component-library";
import { LBFooter } from "components";
import { getSponsorCompanies } from "store/state/companies";
import moment from "moment";
import numberFormatter from "utils/numberFormatter";
import { format } from "date-fns";

import SreoUploadModal from "./SREOUploadModal";
import {
  getCompanies,
  downloadUploadTemplate,
  importSreoProperties,
  getSreoProperties,
  exportGrid
} from "./screener.store";
import SreoScreenerTable from "./SreoScreenerTable";
import SreoTextFilter from "./SreoTextFilter";
import SreoSelectFilter from "./SreoSelectFilter";
import SreoEmptyView from "./SreoEmptyView";
import SreoScreenerHeader from "./SreoScreenerHeader";

const StyledGridWrapper = styled.div`{
    overflow: unset;
    max-width: 1340px;
    margin: 0 auto;
    padding: 10px 6px;
}`;

const Row = styled.div`{
    width: 100%;
}`;

const TableWrapper = styled.div`
    overflow-x: scroll;
    height: 72vh;
    box-shadow: 0px 10px 20px 0px #ADAEAE40;
    border: 1px solid #DADCE0;

    table {
        position: relative;
        width: 100%;
        border-collapse: seperate;
        border-spacing: 0;
        overflow-x: scroll;
        font-family: 'Work Sans';
        margin-bottom: 30px;
    }

    // First Row
    table th {
        position: sticky;
        top: 0;
        z-index: 3;
    }

    // Column Names row
    table thead tr:nth-child(2) {
        position: sticky;
        top: 0;
        z-index: 5;
    }

    table thead tr:nth-child(2) th {
        white-space: pre-wrap;
    }

    // First Column, Second Header Row, First Cell ("Property Name" Cell)
    table thead tr:nth-child(2) th:nth-child(1) {
        left: 0;
        z-index: 4;
        background-color: #FAFAFA;
    }

    // First Column Table Cells ("Property Name" Column Cells)
    table tbody tr td:nth-child(1) {
        position: sticky;
        left: 0;
        z-index: 2;
        background-color: inherit;
    }

    thead tr {
        background: #FAFAFA;
        box-sizing: border-box;
        height: 64px;
    }

    thead tr th {
        border: 1px solid #DADCE0;
    }

    thead tr:first-child {
        color: #5F6368;
        text-align: left;
        height: 48px;
        text-transform: uppercase;
        font-size: 12px;
        line-height: 14px;
    }

    thead tr:first-child th {
        padding-left: 16px;
    }

    thead tr:nth-child(2) {
        font-size: 14px;
        font-weight: 300;
        text-transform: uppercase;
        letter-spacing: 0.4px;
    }

    tbody tr {
        height: 64px;
    }

    table tbody tr:hover {
        background-color: #b7b7e8;
    }

    // Column Base Style
    tbody tr td {
        border: 1px solid #DADCE0;
        min-width: 170px;
        padding: 0px 16px;
        text-align: center;
        font-size: 14px;
    }

    // Property Name Column
    tbody tr td:first-child {
        text-align: left;
        color: #005AA9;
        font-size: 14px;
        line-height: 16px;
        font-weight: 400;
    }

    // Units, Year Built, Occupancy, Year Acquired Columns
    tbody tr td:nth-child(n+8):nth-child(-n+11) {
        font-family: "Space Mono";
    }

    // Company Ownership % Column
    tbody tr td:nth-child(13) {
        font-family: "Space Mono";
    }

    // Maturity, Outstanding Loan Amount, Annual Debt Service Columns
    tbody tr td:nth-child(n+16):nth-child(-n+18) {
        font-family: "Space Mono";
    }

    // Outstanding Loan Amount, Annual Debt Service Columns
    tbody tr td:nth-child(n+17):nth-child(-n+18) {
        text-align: right;
    }

    // EGI, OPEX, Current NOI, DSCR, Market Value, Cap Rate, Value per Unit, LTV Columns
    tbody tr td:nth-child(n+20) {
        font-family: "Space Mono";
    }

    // EGI, OPEX, Current NOI Columns
    tbody tr td:nth-child(n+20):nth-child(-n+22) {
        text-align: right;
    }

    // Market Value Column
    tbody tr td:nth-child(24) {
        text-align: right;
    }

    // Value per Unit Column
    tbody tr td:nth-child(26) {
        text-align: right;
    }

    tbody tr:nth-of-type(odd) {
        background-color: #FFFFFF;
    }

    tbody tr:nth-of-type(even) {
        background-color: #F5F5F5;
    }
`;

const StyledButtonText = styled(ButtonText)`&&&&{
 .material-icons {
    transform: rotate(0deg);
    }
}`;

const FooterContainer = styled.div`
    margin: 0 auto;
    padding: 10px 6px;
    background: #f5f5f5;
`;

const SREOScreener = props => {
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedAsOfDate, setSelectedAsOfDate] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [displayFilterButton, setDisplayFilterButton] = useState(true);
  const [numberOfProperties, setNumberOfProperties] = useState(0);
  const [averageDscr, setAverageDscr] = useState(0);
  const [averageCapRate, setAverageCapRate] = useState(0);
  const [averageLtv, setAverageLtv] = useState(0);

  const {
    company,
    downloadUploadTemplate,
    exportGrid,
    getSponsorCompanies,
    getSreoProperties,
    history,
    importError,
    importSreoProperties,
    properties: sreoProperties
  } = props;

  const { properties = [] } = sreoProperties || {};

  const numberOfUnits = properties.reduce((a, b) => a + parseInt(b.units), 0);
  const totalOpex = properties.reduce((a, b) => a + parseFloat(b.opEx), 0);
  const totalEgi = properties.reduce((a, b) => a + parseFloat(b.egi), 0);
  const averageExpenseRatio = totalOpex / totalEgi;
  const totalDebt = properties
    .map(property => property.loans.reduce((a, b) => a + parseFloat(b.outstandingLoanAmount), 0))
    .reduce((a, b) => a + b, 0);
  const totalDscr = properties.reduce((a, b) => a + parseFloat(b.dscr), 0);
  const totalCapRate = properties.reduce((a, b) => a + parseFloat(b.capRate), 0);
  const totalLtv = properties.reduce((a, b) => a + parseFloat(b.ltv), 0);

  const noProperties = properties && properties.length;

  useEffect(() => {
    getSponsorCompanies();
    getSreoProperties({ sponsorCompanyId: "", asOfDate: "" });
  }, []);

  useEffect(() => {
    const totalProperties = properties.length;
    noProperties ? setDisplayFilterButton(false) : setDisplayFilterButton(true);
    setNumberOfProperties(totalProperties);
    setAverageDscr(totalDscr / totalProperties);
    setAverageCapRate(totalCapRate / totalProperties);
    setAverageLtv(totalLtv / totalProperties);
  }, [sreoProperties]);

  const handleCompanyDropdown = value => {
    getSreoProperties({ sponsorCompanyId: value, asOfDate: "" });
    setSelectedCompanyId(value);
    setSelectedAsOfDate("");
  };

  const handleDateDropdown = value => {
    getSreoProperties({ sponsorCompanyId: selectedCompanyId, asOfDate: value.trim() });
    setSelectedAsOfDate(value);
  };

  const handleTemplateRequest = (companyId, asOfDate) => {
    if (!companyId) {
      setErrorMessage("Missing Required Input: Company Id");

      return;
    }

    if (!asOfDate) {
      setErrorMessage("Missing Required Input: As Of Date");

      return;
    }

    asOfDate = moment.utc(asOfDate).format("MM/DD/YYYY");
    downloadUploadTemplate({ companyId, asOfDate });
    setErrorMessage("");
  };

  const handleUpload = (file, companyId, asOfDate) => {
    if (!companyId) {
      setErrorMessage("Missing Required Input: Company Id");

      return;
    }
    if (!asOfDate) {
      setErrorMessage("Missing Required Input: As Of Date");

      return;
    }
    asOfDate = moment(asOfDate).format("MM/DD/YYYY");
    importSreoProperties({ file, companyId, asOfDate, setUploadModalOpen });
    setErrorMessage("");
  };

  const formatMoneyCells = element => {
    const value = (element.value) ? numberFormatter.formatDecimal(element.value, 2) : "--";

    return (<div style={{ display: "flex", flexDirection: "row", textAlign: "justify" }}>
      <span style={{ flex: 1, fontSize: "larger" }}>$</span>
      <span style={{ flex: 1 }}>{value}</span>
            </div>);
  };

  const formatPercentage = element => {
    const value = (element.value) ? numberFormatter.formatRate(element.value, 2) : "--";

    return (<div>{value}</div>);
  };

  const formatDscr = element => {
    const value = (element.value) ? `${numberFormatter.formatDecimal(element.value, 2)}X` : "--";

    return (<div>{value}</div>);
  };

  const formatDate = element => {
    const value = (element.value) ? format(new Date(element.value), "MM/dd/yyyy") : "--";

    return (<div>{value}</div>);
  };

  const defaultValue = element => {
    const value = (element.value) ? element.value : "--";

    return (<div>{value}</div>);
  };

  const columns = [
    {
      Header: "Property Information",
      columns: [
        { accessor: "propertyName", sortType: "basic", Header: "Property Name", Filter: SreoTextFilter, Cell: element => defaultValue(element) },
        { accessor: "address", sortType: "basic", Header: "Address" },
        { accessor: "city", sortType: "basic", Header: "City", Cell: element => defaultValue(element) },
        { accessor: "state", sortType: "basic", Header: "State", Filter: SreoSelectFilter, width: 90, Cell: element => defaultValue(element) },
        { accessor: "country", sortType: "basic", Header: "Country", Filter: SreoSelectFilter, width: 90, Cell: element => defaultValue(element) },
        { accessor: "status", sortType: "string", Header: "Status", Filter: SreoSelectFilter, width: 140, Cell: element => defaultValue(element) },
        { accessor: "assetType", sortType: "string", Header: "Asset Type", Filter: SreoSelectFilter, width: 140, Cell: element => defaultValue(element) },
        { accessor: "units", sortType: "number", Header: "Units", width: 100, Cell: element => defaultValue(element) },
        { accessor: "yearBuilt", sortType: "number", Header: "Year Built", width: 100, Cell: element => defaultValue(element) },
        { accessor: "occuppancy", sortType: "number", Header: "Occupancy", width: 100, filter: "number", Cell: element => formatPercentage(element) },
        { accessor: "yearAcquired", sortType: "number", Header: "Year Acquired", width: 100, Cell: element => defaultValue(element) }
      ]
    },
    {
      Header: "Ownership",
      columns: [
        { accessor: "companyOwnershipRole", sortType: "string", Header: "Company Ownership Role", Filter: SreoSelectFilter, width: 100, Cell: element => defaultValue(element) },
        { accessor: "ownershipPercentage", sortType: "number", Header: "Company Ownership (%)", filter: "number", width: 100, Cell: element => formatPercentage(element) }
      ]
    },
    {
      Header: "Debt",
      columns: [
        { accessor: "lender", sortType: "string", Header: "Lender", Filter: SreoSelectFilter, Cell: element => defaultValue(element) },
        { accessor: "loanType", sortType: "string", Header: "Loan Type", Filter: SreoSelectFilter, width: 100, Cell: element => defaultValue(element) },
        { accessor: "maturity", sortType: "basic", Header: "Maturity", width: 100, Cell: element => formatDate(element) },
        { accessor: "outstandingLoanAmount", sortType: "number", Header: "Outstanding Loan Amount", Cell: element => formatMoneyCells(element) },
        { accessor: "annualDebtService", sortType: "number", Header: "Annual Debt Service", Cell: element => formatMoneyCells(element) },
        { accessor: "ioOrAmort", sortType: "string", Header: "I/O Or Amoritizing", Filter: SreoSelectFilter, Cell: element => defaultValue(element) }
      ]
    },
    {
      Header: "Cash Flow",
      columns: [
        { accessor: "egi", sortType: "number", Header: "Egi", Cell: element => formatMoneyCells(element) },
        { accessor: "opEx", sortType: "number", Header: "Opex", Cell: element => formatMoneyCells(element) },
        { accessor: "currentNoi", sortType: "number", Header: "Current Noi", Cell: element => formatMoneyCells(element) },
        { accessor: "dscr", sortType: "number", Header: "DSCR", width: 100, Cell: element => formatDscr(element) }
      ]
    },
    {
      Header: "Valuation",
      columns: [
        { accessor: "marketValue", sortType: "number", Header: "Market Value", Cell: element => formatMoneyCells(element) },
        { accessor: "capRate", sortType: "number", Header: "Cap Rate", filter: "number", Cell: element => formatPercentage(element) },
        { accessor: "valuePerUnit", sortType: "number", Header: "Value Per Unit", Cell: element => formatMoneyCells(element) },
        { accessor: "ltv", sortType: "number", Header: "ltv", width: 100, filter: "number", Cell: element => formatPercentage(element) }
      ]
    }
  ];

  const shouldShowDates = (sreoProperties?.dates && selectedCompanyId && sreoProperties.dates);
  const sponsorCompanies = (company?.sponsorCompanies && company?.sponsorCompanies.length > 0) ? company.sponsorCompanies.map(({ id, name }) => ({ id, name })) : [];
  const selectedCompany = selectedCompanyId ?
    sponsorCompanies.filter(company => company.id === selectedCompanyId)[0].name :
    "All Companies";

  const headerData = { selectedCompany, asOfDate: selectedAsOfDate };
  const footerData = { companyId: selectedCompanyId, asOfDate: selectedAsOfDate };
  const dates = (sreoProperties.dates || []).map(val => ({ value: val, momentVal: moment.utc(val.trim()) })).sort((a, b) => b.momentVal - a.momentVal).map(val => val.value);

  return (
    <StyledGridWrapper>
      <Row style={{ paddingTop: "6px", paddingBottom: "15px", paddingLeft: "10px" }}>
        <ButtonText
          onClick={() => history.goBack()}
          iconName="arrow_forward"
          label="Go Back"
          reverse
        />
      </Row>
      <Row>
        <SreoScreenerHeader
          sponsorCompanies={sponsorCompanies}
          selectedCompany={selectedCompany}
          selectedAsOfDate={selectedAsOfDate}
          handleDateDropdown={handleDateDropdown}
          handleCompanyDropdown={handleCompanyDropdown}
          selectedCompanyId={selectedCompanyId}
          dates={dates}
          shouldShowDates={shouldShowDates}
          numberOfProperties={numberOfProperties}
          numberOfUnits={numberOfUnits}
          averageExpenseRatio={averageExpenseRatio}
          totalDebt={totalDebt}
          averageDscr={averageDscr}
          averageCapRate={averageCapRate}
          averageLtv={averageLtv}
        />
      </Row>
      <Row style={{ display: "flex", flexWrap: "wrap", width: "100%", alignItems: "flex-start", paddingTop: "30px", paddingBottom: "10px" }}>
        {
          !displayFilterButton &&
            <div style={{ flexBasis: "50%", flex: "0 1 auto" }}>
              <StyledButtonText
                onClick={() => {
                  setShowFilter(!showFilter);
                }}
                iconName="filter_list"
                label="Filter Table"
                reverse
              />
            </div>
        }
        <div style={{ marginLeft: "auto", flex: "0 1 auto", flexGrow: 0 }}>
          <SecondaryButton
            onClick={() => {
              setUploadModalOpen(true);
            }}
            style={{ fontSize: "16px", lineHeight: "16px", textTransform: "capitalize" }}
          >
            Upload
          </SecondaryButton>
          <SecondaryButton
            disabled={!selectedCompanyId || !sreoProperties?.properties?.length}
            onClick={() => exportGrid({ format: "EXCEL", results: sreoProperties.properties, headerData, footerData })}
            style={{ fontSize: "16px", lineHeight: "16px", textTransform: "capitalize" }}
          >
            Export
          </SecondaryButton>
        </div>
      </Row>
      {
        noProperties ?
          <TableWrapper>
            {sreoProperties?.properties && <SreoScreenerTable columns={columns} data={sreoProperties.properties} showFilter={showFilter} />}
          </TableWrapper> :
          <SreoEmptyView />
      }
      {company && <SreoUploadModal
        companies={company ? sponsorCompanies : []}
        selectedCompany={selectedCompanyId}
        errorMessage={importError || errorMessage}
        isOpen={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
        }}
        onUpload={(file, companyId, asOfDate) => {
          handleUpload(file[0], companyId, asOfDate);
        }}
        onTemplateRequest={(companyId, asOfDate) => {
          asOfDate = moment.utc(asOfDate).format("MM/DD/YYYY");
          handleTemplateRequest(companyId, asOfDate);
        }}
      />}
      <FooterContainer>
        <LBFooter />
      </FooterContainer>
    </StyledGridWrapper>
  );
};

const mapStateToProps = ({ sreoScreenerReducer: sreo, companies }) => ({
  company: {
    companyId: sreo.company.id,
    name: sreo.company.name,
    sponsorCompanies: companies.companySponsorCompanies
  },
  properties: sreo.sreoProperties,
  importError: sreo.importError,
  closeModal: sreo.closeModal
});

export default compose(withRouter, connect(mapStateToProps, {
  getCompanies,
  downloadUploadTemplate,
  importSreoProperties,
  getSreoProperties,
  exportGrid,
  getSponsorCompanies
}))(SREOScreener);
